import { useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence } from 'framer-motion'
import { StorefrontBackdrop, StorefrontNav } from '../storefront/chrome'
import { OperatorSidebar } from './OperatorSidebar'
import { ProfileOverlay } from '../../pages/ProfileOverlay'
import { Reveal } from '../../motion'

/**
 * DashboardShell — single chrome for every dashboard page.
 *
 * Per Kiran 2026-05-13: the dashboard navigation must be visible across
 * ALL dashboard pages (post-login landing /dashboard AND /manage/*
 * AND /iam/*), not just inside one section.
 *
 * Wrappable: any dashboard route renders { children } inside this shell.
 *
 * Mobile-first (SOP 02 §"Mobile-First"):
 *   - md+ : sidebar always visible on the left (static, w-60).
 *   - < md: sidebar collapses to an off-screen drawer (w-72);
 *           hamburger button in the topbar opens it.
 *
 * Sidebar entries come from DASHBOARD_NAV_GROUPS (Rule #14). Adding a
 * new dashboard screen = one constant edit + one route entry.
 *
 * Renamed from AdminShell to DashboardShell 2026-05-14.
 */
export function DashboardShell({ children }: { readonly children: ReactNode }): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  // The drawer stays open after selecting a screen (Kiran 2026-05-21) — it
  // closes ONLY via outside-click (the dim), the 3-line toggle, or Esc.

  // While the drawer is open: Esc closes it and the page scroll is locked.
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 z-0">
        <StorefrontBackdrop />
      </div>
      {/* Full-width top bar; the 3-line button toggles the sidebar drawer,
          the profile icon (before Sign out) opens the profile overlay. */}
      <StorefrontNav onMenu={() => setDrawerOpen((v) => !v)} onProfile={() => setProfileOpen(true)} fullWidth />
      {/* Dim — click anywhere outside the drawer to close it. */}
      {drawerOpen ? (
        <div
          aria-hidden
          className="fixed inset-0 z-30 bg-charcoal/40"
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}
      <OperatorSidebar drawerOpen={drawerOpen} />
      {/* The content region (below the top bar) sits on the cream background
          colour; the background image is retained above it + faint through the
          translucency + in the drawer. Kiran 2026-05-21. */}
      <main className="relative z-10 pt-20">
        <div className="min-h-[calc(100vh-5rem)] bg-bg-primary/70 px-6 pb-8 pt-6 backdrop-blur-sm sm:px-8">
          {/* Per-page entrance reveal — every operator page that mounts
              inside the shell inherits the calm fade-up without per-page
              edits. Restrained-cinematic, Kiran 2026-06-08. */}
          <Reveal amount={0.01}>{children}</Reveal>
        </div>
      </main>

      <AnimatePresence>
        {profileOpen ? <ProfileOverlay onClose={() => setProfileOpen(false)} /> : null}
      </AnimatePresence>
    </div>
  )
}


